import * as THREE from 'three';
import { computeBoundsTree } from 'three-mesh-bvh';
import { BufferGeometryUtils } from 'three/examples/jsm/Addons.js'

/**
 * Slices a mesh by a plane, creating two new meshes ("upper" and "lower").
 * This function implements the robust slicing algorithm that generates new faces
 * along the cut plane for a clean result.
 *
 * @param {THREE.Mesh} mesh The mesh to be sliced.
 * @param {THREE.Plane} plane The THREE.Plane to slice with.
 * @returns {{ upper: THREE.Mesh, lower: THREE.Mesh } | null} An object containing the two new meshes, or null if slicing fails.
 */
export function sliceMesh(mesh, plane) {
  const originalGeom = mesh.geometry;
  const sourceGeom = originalGeom.index ? originalGeom.toNonIndexed() : originalGeom;
  const attributes = sourceGeom.attributes;
  const posAttr = attributes.position;
  const colorAttr = attributes.color;
  const upperData = { position: [], color: [] };
  const lowerData = { position: [], color: [] };

  function getIntersectionData(p1, p2, c1, c2) {
    const line = new THREE.Line3(p1, p2);
    const intersectionPoint = new THREE.Vector3();
    if (!plane.intersectLine(line, intersectionPoint)) {
      return null;
    }

    const distance = p1.distanceTo(p2);
    const alpha = p1.distanceTo(intersectionPoint) / distance;

    const intersectionData = { point: intersectionPoint };

    // --- FIX: Interpolate color ---
    if (c1 && c2) {
      intersectionData.color = c1.clone().lerp(c2, alpha);
    }

    return intersectionData;
  }

  // Iterate over each face
  for (let i = 0; i < posAttr.count; i += 3) {
    // pull out original vertices + colors
    const p1 = new THREE.Vector3().fromBufferAttribute(posAttr, i);
    const p2 = new THREE.Vector3().fromBufferAttribute(posAttr, i+1);
    const p3 = new THREE.Vector3().fromBufferAttribute(posAttr, i+2);
    const c1 = colorAttr ? new THREE.Color().fromBufferAttribute(colorAttr, i) : null;
    const c2 = colorAttr ? new THREE.Color().fromBufferAttribute(colorAttr, i+1) : null;
    const c3 = colorAttr ? new THREE.Color().fromBufferAttribute(colorAttr, i+2) : null;
  
    // compute the original face normal
    const origNormal = new THREE.Vector3()
      .subVectors(p2, p1)
      .cross(new THREE.Vector3().subVectors(p3, p1))
      .normalize();
  
    // wrap your push logic in here
    function pushSmart(dataArray, A, B, C) {
      // A, B, C are objects { point: Vector3, color: Color }
      // build face-normal for this candidate triangle
      const faceNormal = new THREE.Vector3()
        .subVectors(B.point, A.point)
        .cross(new THREE.Vector3().subVectors(C.point, A.point))
        .normalize();
  
      // if it’s flipped relative to orig, swap B/C
      if (faceNormal.dot(origNormal) < 0) {
        dataArray.position.push(
          A.point, C.point, B.point
        );
        if (colorAttr) {
          dataArray.color.push(
            A.color, C.color, B.color
          );
        }
      } else {
        dataArray.position.push(
          A.point, B.point, C.point
        );
        if (colorAttr) {
          dataArray.color.push(
            A.color, B.color, C.color
          );
        }
      }
    }
  
    // split vs. whole logic stays the same, but call pushSmart instead of addFace
    const points = [p1,p2,p3], colors = [c1,c2,c3];
    const distances = points.map(p => plane.distanceToPoint(p));
    const above = [], below = [];
    for (let j = 0; j < 3; j++) {
      if (distances[j] >= 0) above.push({ point: points[j], color: colors[j] });
      if (distances[j] <= 0) below.push({ point: points[j], color: colors[j] });
    }
  
    if (above.length === 3) {
      pushSmart(upperData, ...above);
    } else if (below.length === 3) {
      pushSmart(lowerData, ...below);
    } else if (above.length === 1 && below.length === 2) {
      const lone = above[0], [pA,pB] = below;
      const i1 = getIntersectionData(lone.point, pA.point, lone.color, pA.color);
      const i2 = getIntersectionData(lone.point, pB.point, lone.color, pB.color);
      if (i1 && i2) {
        pushSmart(upperData, lone, i1, i2);
        pushSmart(lowerData, pA, i1, pB);
        pushSmart(lowerData, pB, i2, i1);
      }
    } else if (above.length === 2 && below.length === 1) {
      const lone = below[0], [pA,pB] = above;
      const i1 = getIntersectionData(lone.point, pA.point, lone.color, pA.color);
      const i2 = getIntersectionData(lone.point, pB.point, lone.color, pB.color);
      if (i1 && i2) {
        pushSmart(lowerData, lone, i2, i1);
        pushSmart(upperData, pA, i1, pB);
        pushSmart(upperData, pB, i1, i2);
      }
    }
  }

  function createSlicedMesh(data, name) {
    if (data.position.length === 0) return null;
    
    let geom = new THREE.BufferGeometry();
    geom.setFromPoints(data.position);
    
    // --- FIX: Add the color attribute to the new geometry ---
    if (colorAttr && data.color.length > 0) {
      const flatColors = data.color.flatMap(c => c.toArray());
      geom.setAttribute('color', new THREE.Float32BufferAttribute(flatColors, 3));
    }
    
    // geom.computeVertexNormals();
    // computeBoundsTree.call(geom);
    const tolerance = 0.0001; 
    const healedGeom = BufferGeometryUtils.mergeVertices(geom, tolerance);
    healedGeom.computeVertexNormals();

    const newMesh = new THREE.Mesh(healedGeom, mesh.material);
    newMesh.name = name;
    newMesh.matrix.copy(mesh.matrix);
    newMesh.matrixAutoUpdate = false;
    return newMesh;
  }

  const upperMesh = createSlicedMesh(upperData, mesh.name + "_upper");
  const lowerMesh = createSlicedMesh(lowerData, mesh.name + "_lower");

  return { upper: upperMesh, lower: lowerMesh };
}

// We can also put the plane calculation logic here for cleanliness.
export function computePlaneFromLine(pointA, pointB, camera) {
    const cameraPosition = new THREE.Vector3();
    camera.getWorldPosition(cameraPosition);

    const vecCamToA = new THREE.Vector3().subVectors(pointA, cameraPosition);
    const vecCamToB = new THREE.Vector3().subVectors(pointB, cameraPosition);
    const planeNormal = new THREE.Vector3().crossVectors(vecCamToA, vecCamToB).normalize();

    // Correct the normal's direction to point "up" relative to the screen line
    const ndcA = pointA.clone().project(camera);
    const ndcB = pointB.clone().project(camera);
    const screenLineDir = new THREE.Vector2().subVectors(ndcB, ndcA);
    const screenUpDir = new THREE.Vector2(-screenLineDir.y, screenLineDir.x).normalize();

    const testPoint = new THREE.Vector3().addVectors(pointA, planeNormal);
    const ndcTest = testPoint.clone().project(camera);
    const screenNormalDir = new THREE.Vector2().subVectors(ndcTest, ndcA).normalize();

    if (screenNormalDir.dot(screenUpDir) > 0) {
        planeNormal.negate();
    }

    return new THREE.Plane().setFromNormalAndCoplanarPoint(planeNormal, pointA);
}

/** Point on plane from plane equation: origin = -constant * normal */
const _planeOrigin = new THREE.Vector3();

/**
 * Creates a visible plane mesh from a THREE.Plane (for slice preview / TransformControls).
 * @param {THREE.Plane} plane
 * @param {number} size Half-extent of the plane (width/height = 2*size)
 * @param {THREE.Vector3 | null} anchorOnPlane Optional world-space point on the plane (e.g. midpoint of the two trim clicks). If omitted, uses the point on the plane closest to world origin — which is often far from the model when the socket is offset.
 * @returns {THREE.Mesh}
 */
export function createPlaneMeshFromPlane(plane, size = 15, anchorOnPlane = null) {
  const geometry = new THREE.PlaneGeometry(size * 2, size * 2);
  const material = new THREE.MeshPhongMaterial({
    color: 0xcc3333,
    transparent: true,
    opacity: 0.5,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(geometry, material);
  if (anchorOnPlane) {
    mesh.position.copy(anchorOnPlane);
  } else {
    _planeOrigin.copy(plane.normal).multiplyScalar(-plane.constant);
    mesh.position.copy(_planeOrigin);
  }
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), plane.normal);
  mesh.userData.isSlicingPlane = true;
  return mesh;
}

/**
 * Gets a THREE.Plane from a plane mesh's world matrix (mesh local Z = plane normal, position = point on plane).
 * @param {THREE.Mesh} mesh Plane mesh created with createPlaneMeshFromPlane
 * @param {THREE.Plane} outPlane Optional plane to write into
 * @returns {THREE.Plane}
 */
export function getPlaneFromMesh(mesh, outPlane = new THREE.Plane()) {
  const normal = new THREE.Vector3(0, 0, 1);
  const point = new THREE.Vector3(0, 0, 0);
  normal.transformDirection(mesh.matrixWorld);
  point.setFromMatrixPosition(mesh.matrixWorld);
  return outPlane.setFromNormalAndCoplanarPoint(normal, point);
}